import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { isNullOrEmptyArray } from '../../../util/arrays';
import SingleQuestionAwareCollapsibleOrderableList from '../single';
import * as Ui from './styles';

function MultiQuestionAwareCollapsibleOrderableList({
  /**
   * identificador do cliente selecionado atualmente
   */
  customerId,
  /**
   * identificador da pesquisa principal
   */
  surveyId,
  /**
   * identificador da pergunta principal
   */
  questionId,
  /**
   * define as questões de pesquisas que serão apresentadas
   */
  surveysQuestions = [/* {surveyId, questionId, questionTitle} */],
  /**
   * define as alternativas que deverão ser combinadas inicialmente.
   * formato de dados: [{surveyId, questionId, matchs:[{source, matched, manual}]}]
   */
  matchedAlternatives = [],
  /**
   * conteúdo html ou component para ser renderizado como erro
   */
  errorComponent,
  /**
   * propaga o evento de combinação das alternativas
   * params: surveyId, questionId, matchs = [{source, matched}]
   */
  onChangeMatchedAlternatives,
  /**
   * propaga o evento de match automático
   * params: matchs = [{source, matched}]
   */
  onAutoMatch,
  /**
   * flag indicando se deve ser feito um mapeamento automático
   * das alternativas passadas
   */
  autoMatch = true,
  /**
   * outras propriedas implícitas
   */
  ...props
}) {
  // from redux
  const { mainAlternativesProps } = props;

  // states
  const [questionsData, setQuestionsData] = useState([/* {label, value} */]);
  const [internalDataToMatch, setInternalDataToMatch] = useState([/* { label, value, order } */]);

  // callbacks: initialize datas
  const initialize = useCallback((allQuestionsStates) => {
    // helper for build questions data
    function prepareNewQuestions() {
      // adapt questions for this component
      const newData = allQuestionsStates.map((s) => ({
        label: s.questionTitle,
        value: { surveyId: s.surveyId, questionId: s.questionId },
      }));
      // set question data
      setQuestionsData(newData);
    }
    // prepare customs questions
    if (!isNullOrEmptyArray(allQuestionsStates)) {
      prepareNewQuestions();
    }
  }, []);

  // callbacks: find matchs by question
  const matches = useCallback((quest) => {
    const result = [];
    matchedAlternatives.forEach((a) => {
      if (a.surveyId === quest.value.surveyId && a.questionId === quest.value.questionId) {
        result.push(...a.matchs);
      }
    });

    return result;
  }, [matchedAlternatives]);

  // ao atualizar as questões ou o filtro da pesquisa
  useEffect(() => {
    if (!isNullOrEmptyArray(surveysQuestions)) {
      initialize(surveysQuestions);
    }
  }, [surveysQuestions, initialize]);

  useEffect(() => {
    if (!isNullOrEmptyArray(mainAlternativesProps.alternativas) && isNullOrEmptyArray(internalDataToMatch)) {
      // get alternatives payload
      const alts = mainAlternativesProps.alternativas;
      // build new data to match and calcule new hash
      const newInternalDataToMatch = alts.map((a, x) => ({
        label: a.enunciado,
        value: a.id,
        order: x + 1,
      }));
      setInternalDataToMatch(newInternalDataToMatch);
    }
  }, [internalDataToMatch, mainAlternativesProps]);

  // handle change matchs
  const handleChangeMatchesCallback = React.useCallback((_surveyId, _questionId, _matches) => {
    if (onChangeMatchedAlternatives) onChangeMatchedAlternatives(_surveyId, _questionId, _matches);
  }, [onChangeMatchedAlternatives]);

  // handle match automático
  function handleAutoMatch(matchs) {
    if (onAutoMatch) onAutoMatch(matchs);
  }
  // components wrapper
  const ErrorComponentWrapped = () => (errorComponent ? (<>{errorComponent}</>) : null);

  return (
    <Ui.Container>
      {/* error */}
      <Ui.ErrorSection>
        <ErrorComponentWrapped />
      </Ui.ErrorSection>
      {/* list */}
      <Ui.BodySection className="MultiQuestionAwareCollapsibleOrderableList-Ui-BodySection">
        {questionsData.map((question) => (
          <Ui.CollapsibleListWrapper key={`key-${question.value.surveyId}-${question.value.questionId}`}>
            <SingleQuestionAwareCollapsibleOrderableList
              reorderable
              readOnlyTitle
              customerId={customerId}
              surveyId={question.value.surveyId}
              questionId={question.value.questionId}
              questionTitle={question.label}
              dataToMatch={internalDataToMatch}
              matchedAlternatives={matches(question)}
              onChangeMatchs={handleChangeMatchesCallback}
              enableAutoMatch={autoMatch}
              onAutoMatch={handleAutoMatch}
            />
          </Ui.CollapsibleListWrapper>
        ))}
      </Ui.BodySection>
    </Ui.Container>
  );
}

const mapStateToMainAlternatives = (customerId, surveyId, questionId, alternativesState) => {
  // state key
  const key = { customerId, surveyId, questionId };
  // get alternative state by key
  const a = alternativesState.get(key);
  //
  return {
    running: a ? a.running : false,
    error: a ? a.error : null,
    alternativas: a ? a.payload : [],
  };
};

/**
 * mapeia a store para as propriedades que serão passadas para este
 * componente.
 *
 * Duas propriedades são extraídas e passadas para o componente:
 * questions => obtido da pesquisa selecionada
 *
 * @param {*} state
 * @param {*} props
 */
const mapStateToProps = (state, props) => {
  // get props
  const { customerId, surveyId, questionId } = props;
  // states
  const alternativesState = state.alternatives;
  // create questions (local)
  const mainAlternativesProps = mapStateToMainAlternatives(
    customerId, surveyId, questionId, alternativesState,
  );
  // result
  return {
    mainAlternativesProps,
  };
};

// create component (delegate) and export
export default connect(
  mapStateToProps, null, null, { forwardRef: false },
)(MultiQuestionAwareCollapsibleOrderableList);
